// W32 page shell (legacy AppLayout + AppSidebar2 + PageToolbar + AlternateLayout): a 250 px sidebar on #062626 with a
// 16 px radius inside an 8 px / 4 px gutter, the 28 px logo, the location switcher, L1 rows 44 px (icon column 29 px,
// text #cfd4ce, white when active with the indicator pill), L2/L3 rows 44 px at 13.5 px (legacy's computed q-item
// min-height; its L2 circle sits at 44 px, its L2 and L3 text at 64 px); the drawer closes at 1024 px
// and the toolbar's hamburger opens it over the page. The toolbar is sticky, min-height 44 px: legacy PageToolbar's
// 12 px top padding, a dense 36 px hamburger in a 40 px column, and the title right after it in heading-sm semibold
// (17.6 px, 600; measured live against legacy on 2026-10-13: hamburger at 269/18, title at 301/19).
import { useEffect, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, Circle, Menu } from 'lucide-react';
import { backofficeMetrics } from '@radish/ui-tokens';

export interface NavNode {
  readonly id: string;
  readonly label: string;
  readonly href?: string;
  readonly icon?: ReactNode;
  readonly active: boolean;
  readonly badge?: string;
  /** Things waiting for the person there, as a red count (Radish Direct gate 18: late card charges); none at 0. */
  readonly count?: number;
  /** A branch the page is inside although none of its links is current (legacy `open: route.name.includes(...)`). */
  readonly opened?: boolean;
  readonly children?: readonly NavNode[];
}

/**
 * A red count of things waiting (Radish Direct gate 18, A189: open late card-charge alerts on the sidebar's Online Store
 * and its Online Orders tab). Nothing at 0 or when unknown. The words say what it counts for a screen reader.
 */
export function CountBadge({ count, label = 'waiting', className = '' }: { readonly count?: number | undefined; readonly label?: string; readonly className?: string }) {
  if (count === undefined || count <= 0) return null;
  return (
    <span className={`inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-[#BF1A2F] px-1.5 text-[11px] leading-none font-semibold text-white ${className}`} data-testid="count-badge">
      {count > 99 ? '99+' : String(count)}<span className="sr-only">{` ${label}`}</span>
    </span>
  );
}

type RenderLink = (node: NavNode, className: string, content: ReactNode) => ReactNode;

/** Whether the page is inside the branch: it holds the current link, or the page opens it without one. */
const holdsPage = (node: NavNode): boolean => node.active || node.opened === true || (node.children ?? []).some(holdsPage);

interface NavRowProps {
  readonly node: NavNode;
  readonly depth: number;
  readonly renderLink: RenderLink;
  readonly routeKey: string;
  readonly parentHoldsPage?: boolean;
  /** Set by a top-level row for its second-level branches, which open as an accordion (B2008). */
  readonly accordion?: { readonly open: boolean; readonly toggle: () => void } | undefined;
  /** Whether the row is its parent's first or last child, for the open branch's separators (B2009). */
  readonly first?: boolean;
  readonly last?: boolean;
}

/** The first branch child the page is inside, which legacy opens from the route; null when none. */
const branchHoldingPage = (children: readonly NavNode[]): string | null =>
  children.find((c) => (c.children ?? []).length > 0 && holdsPage(c))?.id ?? null;

function NavRow({ node, depth, renderLink, routeKey, parentHoldsPage = false, accordion, first = false, last = false }: NavRowProps) {
  const holdsActive = holdsPage(node);
  const [ownOpen, setOwnOpen] = useState(holdsActive);
  const open = accordion === undefined ? ownOpen : accordion.open;
  const children = node.children ?? [];
  // The second-level branches under a top-level row share one open slot, as legacy's `q-expansion-item
  // :group="item.name"`: opening one closes its open sibling (B2008). Top-level rows have no group.
  const [openChild, setOpenChild] = useState<string | null>(() => branchHoldingPage(children));
  const holdingChild = branchHoldingPage(children);
  // Legacy rebuilds the menu from the route on every navigation (`watch(route.name)` → `updateMenu`, each `open:
  // route.name.includes(...)`): the branches the page is inside open and every other one closes, also when the frame
  // stays mounted (a link inside the page, the browser's back button) (B2008).
  useEffect(() => {
    setOwnOpen(holdsActive);
    setOpenChild(holdingChild);
  }, [routeKey, holdsActive, holdingChild]);
  // Legacy AppSidebar2 (B2006): a branch header is white only at the second level, while it is open and its top-level
  // branch holds the page (`active-sidebar-link-2` inside `ui-menu-sub-item-open`). A closed branch stays grey although
  // it holds the page, at either level, and gets no pill. Legacy's dark mode whitens every second-level header through
  // Quasar's `.q-item--dark`; Next keeps this rule in both modes (B2007, L1133).
  const lit = node.active || (depth === 1 && children.length > 0 && open && parentHoldsPage);
  const height = 'min-h-11';
  const indent = depth === 0 ? 'pl-4' : depth === 1 ? 'pl-[40px]' : 'pl-[60px]';
  const text = depth === 0 ? 'text-[14px] font-medium' : 'text-[13.5px] font-medium';
  // Legacy AppSidebar2 under the pointer (B2020): a top-level label and icon are white while the pointer is anywhere in
  // its block (`.sidebar-link-1:hover`, so also over the rows it holds); a hovered top-level or second-level row keeps its
  // colour under Quasar's focus helper, the q-item's own colour at 15 % over the row (#cfd4ce in light whatever the label
  // shows, white in dark from `.q-item--dark`); a third-level link turns
  // `--sidebar-menu-hover` in light and white in dark (`body--dark .sidebar-link-3:hover`), untinted.
  // The current third-level link is white in light and `--q-text-high-emph` (white at 87 %) in dark, under the pointer too:
  // legacy's `body--dark .sidebar-link-3.q-router-link--active` comes after its hover rule at the same specificity, both
  // !important, so it wins (B2029).
  const color = lit ? (depth >= 2 ? 'text-white dark:text-white/[0.87]' : 'text-white')
    : depth === 0 ? 'text-sidebar-text group-hover/l1:text-white'
    : depth === 1 ? 'text-sidebar-text'
    : 'text-sidebar-text hover:text-sidebar-menu-hover dark:hover:text-white';
  // The tint is the q-item's own colour (#3112 review, measured live): a branch header and a second-level link are
  // q-items of their own, white in dark (`.q-item--dark`); a top-level link is `sidebar-link-1` itself, whose
  // `.sidebar-list-area .sidebar-link-1 { color: --q-rad-alternate }` beats both `.q-item--dark` and `.q-item--active`, so
  // its tint is the label grey in both themes (white at 50 % in dark); the current second-level link is `.q-item--active`,
  // primary (#4d833d light, #b4d5ac dark).
  const tintColour = children.length > 0 ? 'bg-sidebar-text dark:bg-white'
    : depth === 0 ? 'bg-sidebar-text'
    : node.active ? 'bg-primary'
    : 'bg-sidebar-text dark:bg-white';
  const tint = depth <= 1
    ? <span aria-hidden data-focus-helper className={`pointer-events-none absolute inset-0 ${tintColour} opacity-0 transition-opacity group-hover/row:opacity-15`} />
    : null;
  const content = (
    <>
      {tint}
      {depth === 0 ? <span className="flex w-[29px] shrink-0 items-center">{node.icon}</span> : null}
      {depth === 1 ? <Circle size={8} strokeWidth={2.5} aria-hidden className="mr-3 shrink-0" /> : null}
      <span className="truncate">{node.label}</span>
      {node.badge === undefined ? null : (
        <span className="ml-3 rounded-chip bg-sidebar-badge px-2.5 py-0.5 text-[10px] leading-[14px] uppercase">{node.badge}</span>
      )}
      <CountBadge count={node.count} className="ml-auto" />
    </>
  );
  const rowClass = `relative flex w-full items-center ${height} ${indent} pr-4 ${text} ${color} transition-colors${depth <= 1 ? ' group/row' : ''}`;
  const item = depth === 0 ? 'relative group/l1' : 'relative';
  const indicator = lit && depth === 0 ? <span aria-hidden className="absolute top-1 bottom-1 left-0 w-[5px] rounded-r-[18px] bg-sidebar-active" /> : null;
  if (children.length === 0) {
    return <li className={item}>{indicator}{renderLink(node, rowClass, content)}</li>;
  }
  // Legacy's second-level branches set `expand-separator`: an open one is ruled across the sidebar at its top unless it is
  // the first child and at its bottom unless it is the last (Quasar's `.q-expansion-item__border`), `--q-border` in dark
  // and rgba(0, 0, 0, 0.12) in light. Top-level branches sit alone in their q-lists, so theirs never show (B2009).
  const separator = 'pointer-events-none absolute inset-x-0 h-px bg-black/[0.12] dark:bg-line';
  const ruled = depth === 1 && open;
  return (
    <li className={item}>
      {indicator}
      {ruled && !first ? <span aria-hidden data-testid="nav-separator-top" className={`${separator} top-0`} /> : null}
      {ruled && !last ? <span aria-hidden data-testid="nav-separator-bottom" className={`${separator} bottom-0`} /> : null}
      <button type="button" aria-expanded={open} onClick={() => (accordion === undefined ? setOwnOpen(!ownOpen) : accordion.toggle())} className={rowClass}>
        {content}
        <span className="ml-auto pl-2">{open ? <ChevronDown size={18} aria-hidden /> : <ChevronRight size={18} aria-hidden />}</span>
      </button>
      {open ? (
        <ul>
          {children.map((c, i) => (
            <NavRow
              key={c.id}
              first={i === 0}
              last={i === children.length - 1}
              node={c}
              depth={depth + 1}
              renderLink={renderLink}
              routeKey={routeKey}
              parentHoldsPage={holdsActive}
              accordion={depth === 0 && (c.children ?? []).length > 0
                ? { open: openChild === c.id, toggle: () => setOpenChild(openChild === c.id ? null : c.id) }
                : undefined}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

interface AppFrameProps {
  readonly logoSrc: string;
  readonly nav: readonly NavNode[];
  readonly renderLink: RenderLink;
  readonly locationSwitcher?: ReactNode;
  readonly title: string;
  readonly toolbarEnd?: ReactNode;
  /** The impersonation bar or an availability banner above everything. */
  readonly topBar?: ReactNode;
  readonly children: ReactNode;
  /** Closes the overlay drawer after navigation on narrow screens. */
  readonly routeKey: string;
}

const DRAWER = backofficeMetrics.shell.drawerBreakpoint;
const wide = (): boolean => typeof window === 'undefined' || window.innerWidth > DRAWER;

export function AppFrame({ logoSrc, nav, renderLink, locationSwitcher, title, toolbarEnd, topBar, children, routeKey }: AppFrameProps) {
  const [isWide, setIsWide] = useState(wide);
  const [open, setOpen] = useState(wide);
  useEffect(() => {
    const onResize = () => {
      const next = wide();
      setIsWide(next);
      setOpen(next);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    if (!wide()) setOpen(false);
  }, [routeKey]);

  const sidebar = (
    <aside
      aria-label="Main navigation"
      className="flex h-full w-[242px] flex-col overflow-hidden rounded-sidebar bg-sidebar"
    >
      <div className="px-3 pt-[18px] pb-3">
        <img src={logoSrc} alt="radish" className="h-7 w-auto" />
      </div>
      {/* Legacy's switcher button sits in q-my-sm: 8 px above and below. */}
      {locationSwitcher === undefined ? null : <div className="px-3 py-2">{locationSwitcher}</div>}
      <nav aria-label="Sections" className="min-h-0 flex-1 overflow-y-auto pb-4">
        <ul>
          {nav.map((n) => <NavRow key={n.id} node={n} depth={0} renderLink={renderLink} routeKey={routeKey} />)}
        </ul>
      </nav>
    </aside>
  );

  return (
    <div className="flex h-svh flex-col bg-page">
      {topBar}
      <div className="flex min-h-0 flex-1">
        {open && isWide ? <div className="w-[250px] shrink-0 px-1 py-2">{sidebar}</div> : null}
        {open && !isWide ? (
          <div className="fixed inset-0 z-[1000] flex">
            <div className="py-2 pl-1">{sidebar}</div>
            <button type="button" aria-label="Close navigation" className="flex-1 bg-black/30" onClick={() => setOpen(false)} />
          </div>
        ) : null}
        <main className="flex min-w-0 flex-1 flex-col overflow-y-auto">
          {/* shrink-0: in the scrolling column a long page squeezed the toolbar to 44 px and lifted the page 16 px (lane D6). */}
          <header className="sticky top-0 z-[500] flex min-h-11 shrink-0 items-center bg-surface px-3 pt-3 pb-3" data-testid="page-toolbar">
            <button
              type="button"
              aria-label={open ? 'Hide navigation' : 'Show navigation'}
              aria-expanded={open}
              onClick={() => setOpen(!open)}
              className="inline-flex h-9 w-10 items-center justify-center text-[#8c8d8c] dark:text-white/40 max-[1024px]:justify-start max-[1024px]:pl-[5.5px]"
            >
              {/* Legacy's q-btn `color="font-tertiary"` (#8c8d8c, white 40 % in dark; a non-text glyph at 3.3:1, kept under the AA
                  default) and lucide Menu at its default 2 stroke (B2053). */}
              <Menu size={24} strokeWidth={2} aria-hidden />
            </button>
            {/* Legacy PageToolbar: the 40 px .app-header-hamburger beside a `tw-w-full` .app-header-content, so flex shrinks the
                hamburger by its share of the 40 px overflow (38.5 px at 1024, 37.4 at 600) and the title starts that much
                nearer (B1479). Under 1024 px the wrapper is a block, so legacy's 35 px round q-btn sits at its left, the
                glyph 5.5 px in; from 1024 it is centred. */}
            <div className="flex w-full min-w-0 items-center justify-between" data-testid="page-toolbar-content">
              <h1 className="min-w-0 flex-1 truncate text-heading-sm font-semibold text-text-1" data-testid="page-title">{title}</h1>
              <div className="flex items-center gap-2">{toolbarEnd}</div>
            </div>
          </header>
          {/* Legacy's page card (UiBaseCard .primary-card) pads 24 px each side, 18 px under 600 px (Quasar xs): B805; and
              24 px on top under 600 px against 12 above, where Next's toolbar already gives the 12 (B993). */}
          <div className="flex-1 bg-surface px-[18px] pb-6 max-[600px]:pt-3 min-[600px]:px-6">{children}</div>
        </main>
      </div>
    </div>
  );
}

const AUTH_LINKS = [
  { label: 'Terms of Service', href: 'https://www.getradish.com/policy/terms-of-service' },
  { label: 'Privacy Policy', href: 'https://www.getradish.com/policy/privacy-policy' },
  { label: 'Contact Us', href: 'https://www.getradish.com/contact-us' },
] as const;

/**
 * Legacy AlternateLayout, measured at 1280×800 (D6 rounds 32 and 34): the page background, the Radish logo 200×50 with
 * 32 px under it, then the page's own content (each sign-in page brings its own 400 px card, as legacy's do), centred in
 * an 800 px column, and the 50 px burgundy footer with legacy's three links in 12 px medium white, 24 px apart. Below
 * 600 px the column stacks from the top: the logo 100 px down and the content 90 % wide.
 *
 * B2028 (beside the snapshot at 1600 × 964, light and dark): in dark legacy shows `darkLogoSrc` (its APP_LOGO_FULL_DARK,
 * the white wordmark) and fills the footer with `--q-dark` (#212224) under links at white 87 %. In light legacy's footer
 * sits at the viewport's bottom while the column stays an 800 px block at the top; in dark legacy's `position: relative`
 * pins it under the 800 px column instead, mid-screen on a taller window (a legacy bug, ledger L1137), so Next keeps it at
 * the bottom in both themes.
 */
export function AuthLayout({ logoSrc, darkLogoSrc, children }: { readonly logoSrc: string; readonly darkLogoSrc?: string; readonly children: ReactNode }) {
  const logo = 'mb-8 h-[50px] w-[200px] object-contain max-[600px]:mx-auto max-[600px]:mt-[100px]';
  return (
    <div className="relative min-h-[max(800px,100vh)] bg-page" data-testid="auth-layout">
    <div className="flex min-h-[800px] flex-col items-center justify-center max-[600px]:block" data-testid="auth-column">
      <img src={logoSrc} alt="radish" className={darkLogoSrc === undefined ? logo : `${logo} block dark:hidden`} />
      {darkLogoSrc === undefined ? null : <img src={darkLogoSrc} alt="radish" className={`${logo} hidden dark:block`} data-testid="auth-logo-dark" />}
      <main className="flex w-full flex-col items-center max-[600px]:mx-auto max-[600px]:w-[90%]">{children}</main>
    </div>
      <footer className="absolute inset-x-0 bottom-0 flex h-[50px] items-center justify-center gap-6 bg-rad-medium text-[12px] font-medium text-white dark:bg-card dark:text-white/[0.87]">
        {AUTH_LINKS.map((l) => (
          <a key={l.label} href={l.href} target="_blank" rel="noreferrer">{l.label}</a>
        ))}
      </footer>
    </div>
  );
}
